import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useInvoices, useInvoiceActions, INVOICE_STATUS_LABEL, INVOICE_STATUS_VARIANT, INVOICE_TYPE_LABEL, daysLate, isPayable, type InvoiceRow } from "./api";
import { dateShort, money } from "@/lib/format";
import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { QueryError } from "@/components/shared/QueryError";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FILTERS: [string, string][] = [["open", "En attente"], ["to_send", "À envoyer"], ["overdue", "Échues"], ["partial", "Partiellement payées"], ["paid", "Soldées"], ["credit_notes", "Avoirs"], ["void", "Annulées"], ["all", "Toutes"]];

export default function InvoicesPage() {
  const nav = useNavigate();
  const [filter, setFilter] = useState("open");
  const { data: invoices, isLoading, error } = useInvoices({ filter });
  const [creating, setCreating] = useState(false);
  const [clientId, setClientId] = useState("");
  const actions = useInvoiceActions();
  const { data: clients } = useQuery({ queryKey: ["clients", "pick"], enabled: creating, queryFn: async () => { const { data, error } = await supabase.from("clients").select("id, legal_name, currency").is("deleted_at", null).eq("status", "active").order("legal_name"); if (error) throw error; return data; } });
  const due = invoices?.filter(isPayable).reduce((s, i) => s + Number(i.amount_due), 0) ?? 0;
  const cur = invoices?.[0]?.currency ?? "EUR";
  return (
    <div>
      <PageHeader title="Factures" description="Une facture émise est figée et numérotée sans trou ; toute correction passe par un avoir." actions={<Button onClick={() => setCreating(true)}><Plus className="mr-2 h-4 w-4" />Facture libre</Button>} />
      <Tabs value={filter} onValueChange={setFilter} className="mb-4">
        <TabsList className="flex-wrap">{FILTERS.map(([k, l]) => <TabsTrigger key={k} value={k}>{l}</TabsTrigger>)}</TabsList>
      </Tabs>
      {error ? <QueryError error={error} /> : isLoading ? <p className="text-sm text-muted-foreground">Chargement…</p> : !invoices?.length ? (
        <EmptyState title="Aucune facture" description="Les factures se créent depuis une commande (acompte, solde, montant libre) ou depuis un client (facture libre)." />
      ) : (
        <>
          {filter === "open" || filter === "overdue" ? <p className="mb-2 text-sm text-muted-foreground">{invoices.length} facture{invoices.length > 1 ? "s" : ""} · restant dû <span className="font-semibold text-foreground">{money(due, cur)}</span></p> : <p className="mb-2 text-sm text-muted-foreground">{invoices.length} document{invoices.length > 1 ? "s" : ""}</p>}
          <InvoicesTable invoices={invoices} onOpen={(id) => nav(`/factures/${id}`)} showClient />
        </>
      )}

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent>
          <DialogHeader><DialogTitle>Facture libre</DialogTitle><DialogDescription>Une facture sans commande : lignes saisies à la main, conditions et régime fiscal du client.</DialogDescription></DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="inv-client">Client *</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger id="inv-client"><SelectValue placeholder="Choisir un client" /></SelectTrigger>
              <SelectContent>{clients?.map((c) => <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setCreating(false)}>Annuler</Button><Button disabled={!clientId || actions.free.isPending} onClick={() => actions.free.mutate({ clientId }, { onSuccess: (id) => { setCreating(false); nav(`/factures/${id}/modifier`); } })}>Créer le brouillon</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function InvoicesTable({ invoices, onOpen, showClient = false, compact = false }: { invoices: InvoiceRow[]; onOpen: (id: string) => void; showClient?: boolean; compact?: boolean }) {
  return (
    <div className="glass overflow-x-auto rounded-2xl">
      <Table>
        <TableHeader><TableRow><TableHead>N°</TableHead><TableHead>Type</TableHead>{showClient && <TableHead>Client</TableHead>}{!compact && <TableHead>Objet</TableHead>}<TableHead>Date</TableHead><TableHead>Échéance</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Restant dû</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
        <TableBody>
          {invoices.map((i) => {
            const late = daysLate(i);
            return (
              <TableRow key={i.id} className="cursor-pointer" onClick={() => onOpen(i.id)}>
                <TableCell className="font-mono text-xs">{i.number ?? "brouillon"}</TableCell>
                <TableCell className="text-muted-foreground">{INVOICE_TYPE_LABEL[i.type]}</TableCell>
                {showClient && <TableCell className="font-medium">{i.clients?.legal_name}</TableCell>}
                {!compact && <TableCell className="text-muted-foreground">{i.title ?? "—"}</TableCell>}
                <TableCell>{dateShort(i.issue_date)}</TableCell>
                <TableCell>{i.due_date ? <>{dateShort(i.due_date)}{late > 0 && <span className="ml-1 text-xs font-medium text-destructive">+{late} j</span>}</> : "—"}</TableCell>
                <TableCell className="whitespace-nowrap text-right tabular-nums">{i.type === "credit_note" ? "− " : ""}{money(i.total, i.currency)}</TableCell>
                <TableCell className="whitespace-nowrap text-right tabular-nums">{i.type === "credit_note" || i.status === "draft" || i.status === "void" ? "—" : money(i.amount_due, i.currency)}</TableCell>
                <TableCell><Badge variant={INVOICE_STATUS_VARIANT[i.status]}>{INVOICE_STATUS_LABEL[i.status]}</Badge></TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
